import { Loader } from '../../components/Loader';
import { Button, Typography } from '../../components/primitives';
import { CheckoutTermsAndConditions } from '../../components/text/CheckoutTermsAndConditions';
import Alert from '../../components/ui/Alert';
import { useCheckoutContext } from '../../context/checkout.context';
import { useSettingsHook } from '../../hooks/settingsStorageHook';
import { testId } from '../../utils';
import { useProductDetails } from '../../utils/project/product';
import { Discount, DiscountText } from './discount';
import * as RadAccordion from '@radix-ui/react-accordion';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import React from 'react';

interface OrderDetailsProps {
  braintreeError: Error | undefined;
  paymentRequestable: boolean;
}

const CheckoutLoaded = ({ children }: { children: React.JSX.Element }) => {
  const { checkoutIsLoading, checkoutIsError } = useCheckoutContext();

  if (checkoutIsLoading || checkoutIsError)
    return <Loader variant="bars" size="sm" />;

  return children;
};

type OrderSummaryProps = OrderDetailsProps & {
  'data-testid': string;
};

interface ShippingTotalProps {
  checkoutSummary: Cornerstone.CheckoutSummary | undefined;
  currencyFormatter: (value?: Cornerstone.MonetaryAmount) => string;
  'data-testid': string;
}

const ShippingTotal: React.FC<ShippingTotalProps> = ({
  checkoutSummary,
  currencyFormatter,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.checkout' });

  const shippingBase = checkoutSummary?.shippingTotal;

  if (!shippingBase) {
    return (
      <Typography
        variant="body"
        {...testId(dataTestId, 'shippingNeedsAddress')}
      >
        {t('enter_address')}
      </Typography>
    );
  }
  const shippingDiscount = checkoutSummary?.shippingDiscountTotal;
  if (!shippingDiscount || shippingDiscount.amount === 0) {
    return (
      <Typography
        variant="body-semibold"
        {...testId(dataTestId, 'shipping-base')}
      >
        {currencyFormatter(shippingBase)}
      </Typography>
    );
  }

  const shippingTotalAmount: Cornerstone.MonetaryAmount = {
    amount: shippingBase.amount - shippingDiscount.amount,
    currency: shippingBase.currency,
  };
  return (
    <div className="flex flex-row gap-1">
      <Typography
        variant="body-semibold"
        className="line-through"
        {...testId(dataTestId, 'shipping-base')}
      >
        {currencyFormatter(shippingBase)}
      </Typography>
      <DiscountText
        {...testId(dataTestId, 'shipping-discounted')}
        currencyFormatter={currencyFormatter}
        discount={shippingTotalAmount}
      />
    </div>
  );
};

const OrderSummary: React.FC<OrderSummaryProps> = ({
  'data-testid': dataTestId,
  ...orderDetailsProps
}) => {
  const { paymentRequestable } = orderDetailsProps;
  const { onOrder, orderData, orderError, checkoutData, checkoutIsLoading } =
    useCheckoutContext();
  const { currencyFormatter } = useSettingsHook();

  const { t } = useTranslation(undefined, { keyPrefix: 'route.checkout' });

  return (
    <div {...testId(dataTestId)}>
      <div className="flex justify-between items-center pb-2">
        <Typography variant="title-small">{t('order_summary')}</Typography>
        <Typography
          variant="footnote"
          className="text-gray-darkest"
        >{`${t('currency')}: ${checkoutData.currency}`}</Typography>
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex justify-between">
          <Typography variant="body-semibold">{t('subtotal')}</Typography>
          {
            <CheckoutLoaded>
              <Typography
                {...testId(dataTestId, 'subtotal')}
                variant="body-semibold"
              >
                {currencyFormatter(checkoutData.subtotal)}
              </Typography>
            </CheckoutLoaded>
          }
        </div>
        <Discount
          {...testId(dataTestId, 'discount')}
          {...orderDetailsProps}
          currencyFormatter={currencyFormatter}
        />
        <div className="flex justify-between">
          <Typography variant="body-semibold">{t('shipping')}</Typography>
          <CheckoutLoaded>
            <ShippingTotal
              checkoutSummary={checkoutData}
              currencyFormatter={currencyFormatter}
              data-testid={dataTestId}
            />
          </CheckoutLoaded>
        </div>
        <div className="flex justify-between">
          <Typography variant="body-semibold">{t('tax')}</Typography>

          <CheckoutLoaded>
            {checkoutData.taxTotal !== undefined ? (
              <Typography
                variant="body-semibold"
                {...testId(dataTestId, 'taxTotal')}
              >
                {currencyFormatter(checkoutData.taxTotal)}
              </Typography>
            ) : (
              <Typography variant="body" {...testId(dataTestId, 'taxTotal')}>
                {t('enter_address')}
              </Typography>
            )}
          </CheckoutLoaded>
        </div>
      </div>
      <hr className="mt-4 mb-2" />
      <div className="flex justify-between">
        <Typography variant="body-semibold">{t('total')}</Typography>
        {
          <CheckoutLoaded>
            <Typography
              {...testId(dataTestId, 'total')}
              variant="body-semibold"
            >
              {currencyFormatter(checkoutData.total)}
            </Typography>
          </CheckoutLoaded>
        }
      </div>
      <hr className="mt-2" />
      <div className="my-4">
        <CheckoutTermsAndConditions />
      </div>
      {/* remove this when new page created */}
      {orderData && (
        <Alert {...testId(dataTestId, 'success')} variant="success">
          {`Payment was successful. Your order number is ${orderData.orderId}.`}
        </Alert>
      )}
      {orderError && (
        <Alert {...testId(dataTestId, 'error')} variant="error">
          {t('order_error')}
        </Alert>
      )}
      <Button
        {...testId(dataTestId, 'place-order')}
        className="mt-2 md:mt-0 mb-2"
        disabled={!paymentRequestable || checkoutIsLoading}
        onClick={onOrder}
      >
        {t('place_order')}
      </Button>
    </div>
  );
};

type ProductItemProps = {
  'data-testid': string;
  item: Cornerstone.CheckoutLineItem;
};

const ProductItem: React.FC<ProductItemProps> = ({
  item,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route' });
  const sku = item.sku;
  const { productDetailsStatus, details } = useProductDetails(sku);
  const { currencyFormatter } = useSettingsHook();

  const detailList = React.useMemo(() => {
    return details.reduce(
      (acc, [key, value]) => {
        if (key === 'Cover' || key === 'Size' || key === 'ProductClass') {
          acc[key] = value;
        }
        return acc;
      },
      { Cover: '', Size: '', ProductClass: '' },
    );
  }, [details]);

  return (
    <div className="flex gap-6 pr-8" {...testId(dataTestId)}>
      {productDetailsStatus === 'error' && (
        <Alert variant="error" data-testid="product-rules-error">
          {t('cart.show_details_error')}
        </Alert>
      )}
      <div className="relative">
        <img
          className="max-w-[90px] max-h-[90px] object-scale-down"
          src={item.thumbnailLargeUrl ?? ''}
          alt={item.title}
        />
        <div className="absolute -top-2 -right-2 bg-gray-light rounded-full flex gap-1 py-[3px] px-[10px]">
          <Typography variant="body">{item.quantity}</Typography>
        </div>
      </div>
      <div
        {...testId('products', 'item', 'details')}
        className="flex flex-col justify-between"
      >
        <div className="flex flex-col">
          <Typography variant="footnote-semibold">
            {`${detailList.ProductClass} (${detailList.Cover})`}
          </Typography>
          <Typography variant="footnote">{detailList.Size}</Typography>
          {(item?.pageCount ?? 0) > 0 && (
            <Typography variant="footnote">
              {t('checkout.pageCount', { pageCount: item.pageCount })}
            </Typography>
          )}
        </div>
        <Typography variant="footnote">
          {(item.quantity || 0) > 1
            ? t('checkout.priceEach', {
                price: currencyFormatter(item.unitPrice),
              })
            : null}
        </Typography>
        <Typography variant="footnote">
          <span
            className={
              item.lineItemDiscount !== undefined &&
              item.lineItemDiscount.amount > 0
                ? 'line-through'
                : ''
            }
          >
            {currencyFormatter(item.lineItemPrice)}
          </span>
          {item.lineItemDiscount !== undefined &&
            item.lineItemDiscount.amount > 0 && (
              <span className="text-green-dark ml-1">
                (
                {currencyFormatter({
                  amount:
                    item.lineItemPrice.amount - item.lineItemDiscount.amount,
                  currency: item.lineItemPrice.currency,
                })}
                )
              </span>
            )}
        </Typography>
      </div>
    </div>
  );
};

type ProductsProps = OrderDetailsProps & {
  'data-testid': string;
};

const Products: React.FC<ProductsProps> = ({ 'data-testid': dataTestId }) => {
  const { checkoutData } = useCheckoutContext();

  return (
    <CheckoutLoaded>
      <div className="flex flex-col gap-4" {...testId(dataTestId)}>
        {checkoutData.lineItems.map((item) => (
          <ProductItem
            {...testId(dataTestId, 'item')}
            key={item.sku + '-' + item.projectId}
            item={item}
          />
        ))}
      </div>
    </CheckoutLoaded>
  );
};

export const OrderDetails: React.FC<OrderDetailsProps> = (props) => {
  const { braintreeError, paymentRequestable } = props;
  const { t } = useTranslation(undefined, { keyPrefix: 'route.checkout' });

  return (
    <div className="flex flex-col">
      <div className="flex flex-col rounded-md shadow-md p-4 space-y-2 w-[476px] bg-white">
        <OrderSummary
          {...testId('order-summary')}
          braintreeError={braintreeError}
          paymentRequestable={paymentRequestable}
        />
        <hr />
        <RadAccordion.Root className="w-full" type="single" collapsible>
          <RadAccordion.Item value="products">
            <RadAccordion.Trigger className="w-full flex justify-between items-center group mb-4">
              <Typography variant="title-small">{t('products')}</Typography>
              <span className="material-symbols-outlined transition-transform duration-500 ease-in-out group-data-[state=open]:rotate-180">
                {'expand_less'}
              </span>
            </RadAccordion.Trigger>
            <RadAccordion.Content>
              <Products {...props} {...testId('order-products')} />
            </RadAccordion.Content>
          </RadAccordion.Item>
        </RadAccordion.Root>
      </div>
    </div>
  );
};

export const MobileOrderDetails: React.FC<OrderDetailsProps> = (props) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.checkout' });

  return (
    <div className="flex flex-col h-full p-4 bg-neutral-100">
      <OrderSummary {...props} {...testId('mobile-order-summary')} />
      <hr className="my-4" />
      <RadAccordion.Root className="w-full" type="single" collapsible>
        <RadAccordion.Item value="products">
          <RadAccordion.Trigger className="w-full flex justify-between items-center group mb-4">
            <Typography variant="title-small">{t('products')}</Typography>
            <span className="material-symbols-outlined transition-transform duration-500 ease-in-out group-data-[state=open]:rotate-180">
              {'expand_less'}
            </span>
          </RadAccordion.Trigger>
          <RadAccordion.Content>
            <Products {...props} {...testId('order-products')} />
          </RadAccordion.Content>
        </RadAccordion.Item>
      </RadAccordion.Root>
    </div>
  );
};
